import { useEffect, useState } from 'react';

import { fontStyles } from '@/styles/typography';

const EXIT_DURATION_MS = 300;

type HomepageHeadingProps = {
  hidden: boolean;
  /** Entrance animation is opt-in so the server-rendered heading does not fade in on load. */
  animateEntrance: boolean;
};

/**
 * Collapses out of the layout when a wallet connects and slides back in when
 * the last one disconnects. The grid wrapper animates the row height so the
 * content below moves with it.
 */
export const HomepageHeading = ({ hidden, animateEntrance }: HomepageHeadingProps) => {
  const [mounted, setMounted] = useState(!hidden);
  if (!hidden && !mounted) setMounted(true);

  // Unmount once the exit settles; a reconnect during the exit clears the timer
  // and the transition simply reverses from wherever it is.
  useEffect(() => {
    if (!hidden) return;
    const timeout = setTimeout(() => setMounted(false), EXIT_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [hidden]);

  if (!mounted) return null;

  return (
    <div
      aria-hidden={hidden || undefined}
      className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none
        ${hidden ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'}
        ${animateEntrance ? 'starting:grid-rows-[0fr]' : ''}`}
    >
      {/* Not clipped: the row collapses under the text while it fades, so the
          fade stays visible over the content sliding up. */}
      <div className="min-h-0">
        <h1
          className={fontStyles({
            variant: 'H2',
            class: [
              'text-[26px] tablet:text-[34px] leading-tight font-semibold',
              'text-center mb-6 tablet:mb-8 px-4',
              'transition-[opacity,translate] duration-300 ease-out motion-reduce:transition-none',
              hidden ? 'opacity-5 translate-y-1' : 'opacity-100 translate-y-0',
              animateEntrance ? 'starting:opacity-5 starting:translate-y-3' : '',
            ],
          })}
        >
          Swap any token <span className="text-token-gray-700">in seconds</span>
        </h1>
      </div>
    </div>
  );
};
